<template>
  <el-form :model="form" class="form-wrapper" ref="form" label-position="top" :rules="formRules">
    <el-row :gutter="30" class="withdraw-top-row">
      <el-col :span="24">
        <div class="account-number">
          <el-form-item class="account-number-field" prop="formAccount">
            <el-select
              v-if="accountOptions.length == 0"
              :value="null"
              popper-class="account-select-dropdown"
              :placeholder="$t('pcsPayment.selectAnAcc')"
              class="custom-select"
            >
              <template #empty>
                <div class="select-acc">{{ $t('common.formValidation.noEligibleAccounts') }}</div>
                <!-- <div class="empty-content">
                  <img src="@/assets/images/withdraw/alert.png" alt="No Data" class="empty-image" />
                  <p>{{ $t('pcsPayment.selectAnAcc') }}</p>
                </div> -->
              </template>
            </el-select>

            <el-select
              v-else
              :value="null"
              placeholder=""
              popper-class="account-select-dropdown"
              @change="selectAccountChange"
              data-testid="formAccount"
            >
              <el-option
                v-for="item in accountOptions"
                :key="item.account"
                :value="JSON.stringify(item)"
                :data-testid="item.account"
              >
                <span>
                  <img
                    :src="getLogo(item.currency)"
                    class="account-flag"
                    alt=""
                    :class="{ 'mr-2': ['ar'].includes(lang) }"
                  />
                  {{ getCurrencySymbol(item) }}
                  <span v-if="participatedTCAccount.includes(item.account)" class="ml-2"><TrophyIcon /></span>
                </span>
              </el-option>
              <template #prefix>
                <div v-if="form.formAccount">
                  <img
                    :src="getLogo(form.currency)"
                    class="account-flag"
                    alt=""
                    :class="{ 'mr-2': ['ar'].includes(lang) }"
                  />
                  {{ getCurrencySymbol(JSON.parse(form.formAccount)) }}
                </div>
                <div v-else>
                  <div class="select-acc">{{ $t('pcsPayment.selectAnAcc') }}</div>
                </div>
              </template>
            </el-select>
            <slot name="accountNumber"></slot>
          </el-form-item>
        </div>
        <div class="amount w-full">
          <numeric-input
            name="amount"
            ref="numberInput"
            v-model="form.amount"
            :currency="form.currency"
            :precision="amountPresition"
            :placeholder="$t('pcsPayment.enterAnAcc')"
            :emptyValue="0"
            class="input-amount"
          >
            <template v-slot:suffix>
              <div class="flex items-stretch justify-between gap-2">
                <div>
                  <span v-if="form.amount == '' && fullAmount !== null" class="range">{{ fullAmount.toFixed(2) }}</span>
                  <span v-else-if="form.amount == '' && form.formAccount" class="range"
                    >{{ min.toFixed(2) }}-{{ max.toFixed(2) }}</span
                  >
                  <span class="currency">{{ form.currency | currencyConvert }}</span>
                </div>
                <el-button
                  class="text-black pointer-events-auto p-0 min-h-10"
                  v-show="showMaxBtn"
                  type="text"
                  @click="maxBalances"
                >
                  {{ $t('common.button.max') }}
                </el-button>
              </div>
            </template>
          </numeric-input>
        </div>
        <slot name="amount"></slot>
      </el-col>
      <el-col :span="24">
        <div class="btn-wrapper">
          <el-button class="btn" type="primary" :disabled="btnDisabled" @click="submit" data-testid="submit">
            <span> {{ $t('common.button.continue') }}</span>
          </el-button>
        </div>
      </el-col>
    </el-row>
    <slot name="footer"></slot>
  </el-form>
</template>

<script>
import NumericInput from '@/components/NumericInput';
import TrophyIcon from '@/components/icon/Trophy.vue';
import splitThousands from '@/util/splitThousands';
import { mapObject } from '@/util/payment/index.js';
import promotionMixin from '@/mixins/promotion';

export default {
  name: 'paymentForm',
  mixins: [promotionMixin],
  props: {
    btnDisabled: {
      type: Boolean,
      default: false
    },
    validAmount: {
      type: Function,
      default: () => {}
    },
    accountNumberOptions: {
      typeof: Array,
      default: []
    },
    max: {
      type: Number,
      default: 99999999.99
    },
    min: {
      type: Number,
      default: 10
    },
    fullAmount: {
      type: [Number, null],
      default: null
    },
    showMaxBtn: {
      type: Boolean,
      default: true
    }
  },
  components: { NumericInput, TrophyIcon },
  data() {
    return {
      form: {
        formAccount: '{}',
        account: '',
        amount: 0,
        currency: null,
        balance: null
      }
    };
  },
  watch: {
    'form.formAccount'(value) {
      const jsonParse = JSON.parse(value);
      this.form.account = jsonParse.account;
      this.form.balance = jsonParse.balance;
      if (this.form.currency === jsonParse.currency) {
        this.$refs.numberInput.updateCurrency(true);
      }
      this.form.currency = jsonParse.currency;
      this.$emit('accountNumberChange', value);
      this.$emit('accountCurrencyChange', this.form.currency);
      this.$refs['form'].fields.find(f => f.prop == 'amount').resetField();
    },
    'form.amount'(value) {
      const errMsg = this.getAmountErrorMessage();
      this.$emit('amountChange', value, errMsg);
    }
  },
  computed: {
    amountPresition() {
      switch (this.form.currency) {
        case 'BTC':
        case 'ETH':
          return 4;
        case 'USC':
          return 0;
        default:
          return 2;
      }
    },
    lang() {
      return this.$store.state.common.lang;
    },
    accountOptions() {
      const paramsMapping = {
        mt4_account: 'account',
        currency: 'currency',
        balance: 'balance',
        accountTypeDisplay: 'accountType',
        login: 'account',
        accountMt4TypeDisplay: 'accountType',
        userId: 'userId',
        mtAccountTypeDisplay: 'mtAccountTypeDisplay',
        platform: 'platform'
      };
      let list = [];
      this.accountNumberOptions.forEach(element => {
        list.push(mapObject(element, paramsMapping));
      });
      if (list.length > 0) {
        let defaultAccountNumIndex = list.findIndex(
          item => item.account === this.$store.state.payment.defaultDepositAccountNum
        );
        if (defaultAccountNumIndex === -1) defaultAccountNumIndex = 0;
        this.form.formAccount = JSON.stringify(list[defaultAccountNumIndex]);
      }
      return list;
    },
    formRules() {
      return {
        formAccount: [
          {
            required: true,
            message: this.$t('common.formValidation.accReq'),
            trigger: 'change'
          }
        ],
        amount: [
          {
            required: true,
            message: this.$t('common.formValidation.amtReq'),
            trigger: 'blur'
          },
          {
            validator: (rule, value, callback) => {
              this.validAmount(value, callback);
            },
            trigger: 'change'
          }
        ],
        withdrawalType: [
          {
            required: true,
            message: this.$t('common.formValidation.withdrawMethodReq'),
            trigger: 'change'
          }
        ]
      };
    }
  },
  methods: {
    validate() {
      return this.$refs['form'].validate;
    },
    submit() {
      this.$refs['form'].validate(async valid => {
        this.$emit('submit', valid);
      });
    },
    getCurrencySymbol(item) {
      let precision = item.currency === 'BTC' || item.currency === 'ETH' ? 8 : 2;
      return this.setAccountType({
        account: item.account,
        currencySymbol:
          item.currency === 'USC' && item.balance <= 0 ? '' : this.$options.filters.currencyToSymbol(item.currency),
        balance: splitThousands(item.balance, precision),
        currency: item.currency,
        accountType: item.accountType,
        regulator: this.regulator
      });
    },
    setAccountType({
      account = '',
      currency = '',
      currencySymbol = '',
      balance = '',
      accountType = '',
      regulator = ''
    }) {
      let newAccountType = accountType;
      if (newAccountType == 'MTS ST') {
        newAccountType = 'Copy Trading';
      }
      if (balance || balance === 0) balance = balance + ' ';
      if (currencySymbol) currencySymbol += ' ';
      return `${account} - ${currencySymbol}${balance}${this.$options.filters.currencyConvert(
        currency
      )} - ${newAccountType}`;
    },
    maxBalances() {
      if (this.form.balance % 1 !== 0 && this.form.currency === 'USC' && this.form.balance > 3000) {
        this.form.amount = Math.trunc(this.form.balance);
      } else {
        this.form.amount = this.form.balance;
      }

      this.$refs['form'].validate();
    },
    getLogo(currency) {
      try {
        return require(`@/assets/images/currency/${currency}.png`);
      } catch (error) {
        return require(`@/assets/images/currency/USD.png`);
      }
    },
    selectAccountChange(value) {
      this.form.formAccount = value;
    },
    // 取得 el-form amount error message
    getAmountErrorMessage() {
      const field = this.$refs.form.fields.find(field => field.prop === 'amount');
      return field.validateMessage || null;
    }
  }
};
</script>

<style lang="scss" scoped>
.form-wrapper {
  .withdraw-top-row {
    .title {
      margin-bottom: 10px;
      .icon {
        display: inline-block;
        width: 22px;
        vertical-align: middle;
      }

      .text {
        display: inline-block;
        // font-size: $h4-font-size;
        color: #1d4752;
        vertical-align: middle;
      }
    }

    .el-select:hover {
      /deep/ .el-input__inner {
        border: 1px solid #1e1e1e73;
        transition: background-color 0.3s ease;
      }
    }

    .account-number {
      /deep/ {
        .el-input__prefix {
          color: $black;
          display: flex;
          width: 90%;
          align-items: center;
          white-space: nowrap;
          overflow: hidden;

          .select-acc {
            margin-left: 12px;
            color: #3d3d3d;
          }
        }
        .el-input__inner {
          height: 48px;
          background-color: #3d3d3d0a;
          border-radius: 10px;
          border: 0;
        }
      }
    }

    .amount {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 8px;

      .numeric-input {
        flex: 1;
      }
      /deep/ .el-input__inner {
        font-size: 14px;
        height: 48px;
        background-color: #3d3d3d0a;
        border-radius: 10px;
        // important to overwrite error border
        border: 1px solid transparent !important;

        &:hover {
          border: 1px solid #1e1e1e73;
        }
      }
      .currency {
        color: #3d3d3d73;
        margin-left: 8px;
      }
    }

    .btn-wrapper {
      margin-top: 10px;
      text-align: center;
    }

    .btn {
      width: 100%;
      height: 48px;
      border-radius: 100px;
    }
  }
}

.account-flag {
  width: 20px;
}

/deep/ .input-amount {
  width: 100%;
}

.account-select-dropdown {
  .el-select-dropdown__item {
    height: 48px;
    display: flex;
    align-items: center;
  }
  .el-select-dropdown__item.hover {
    background-color: #f7f7f7;
  }

  .el-scrollbar__view.el-select-dropdown__list {
    padding: 0;
  }
}

.account-flag {
  margin: 7px 0 10px 14px;
}
.account-flag.mr-2 {
  margin: 7px 10px 10px 14px;
}
</style>
